import * as React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { Button, Paper, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import {default as DataGrid} from '../../components/LocalizedDataGrid';
import moment from 'moment-timezone';
import RefreshIcon from '@mui/icons-material/Refresh';
import FloorPlanViewer from "./components/FloorPlanViewer";
import ToggleButton from '@mui/material/ToggleButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import EditIcon from '@mui/icons-material/Edit';
import { Tabs, Tab} from '@mui/material';
import { useTranslation } from "react-i18next";
import { ApiContext } from '../../App';



const TemperatureTable = (props) => {

    const {temperature} = props;

    const { t } = useTranslation();

    const columns = [
        { field: 'controller_name', headerName: t('Controller'), flex:1, minWidth: 80, hide: true},
        { field: 'identifier', headerName: t('ID'), flex:1, minWidth: 200, hide: true},
        { field: 'location', headerName: t('Location'), flex:1, minWidth: 150},
        { field: 'system', headerName: t('System'), flex:1, minWidth: 150, hide: true},
        { field: 'name', headerName: t('Device'), flex:1, minWidth: 150, hide: true},
        { field: 'key', headerName: t('Key'), flex:1, minWidth: 150, valueFormatter: (item)=>t(item.value)},
        { field: 'value', headerName: t('Value'), flex:1, minWidth: 100, valueFormatter: (item)=> typeof(item) === Number ? parseFloat(item.value).toFixed(1): item.value}
      ];

      return (
        <DataGrid 
            columns={columns}
            rows={temperature}
            hideFooter
            getRowId={(row)=>row.url}
        />
    )
}

const ReadingsTable = (props) => {

    const {readings} = props;

    const { t } = useTranslation();

    // skipping rows where battery level is okay
    const filteredReadings = readings.filter((item)=>!((item.key === "BATTERY_LEVEL") && (item.value === "OK")))

    function beautifyKey(k) {
        return t(k)
    }

    function valueGetter(params) {

        if (params.row.key === "MODE"){
            if (parseInt(params.value) === 1) 
                return t("Auto")
            if (parseInt(params.value) === 2) 
                return t("Heat")
            if (parseInt(params.value) === 3) 
                return t("Dry")
            if (parseInt(params.value) === 4) 
                return t("Fan")
            if (parseInt(params.value) === 5) 
                return t("Cool")
            if (parseInt(params.value) === 6) 
                return t("AutoHeat")
            if (parseInt(params.value) === 7) 
                return t("AutoCool")
            if (parseInt(params.value) === 8) 
                return t("Setback")
            if (parseInt(params.value) === 9) 
                return t("Setbackheat")
            if (parseInt(params.value) === 10) 
                return t("Setbackcool")
        }

        if (params.row.key === "FAN_SPEED"){
            if (parseInt(params.value) === 1) 
                return t("Auto")
            if (parseInt(params.value) === 2) 
                return t("Low_fan")
            if (parseInt(params.value) === 3) 
                return t("Middle 2")
            if (parseInt(params.value) === 4) 
                return t("Middle 1")
            if (parseInt(params.value) === 5) 
                return t("High_fan")
        }

        return params.value
        
    }


    const columns = [
        { field: 'controller_id', headerName: t('Controller ID'), flex:1, minWidth: 150, hide:true}, 
        { field: 'controller_name', headerName: t('Controller'), flex:1, minWidth: 150}, 
        { field: 'identifier', headerName: t('Device ID'), flex:1, minWidth: 150, hide:true}, 
        { field: 'location', headerName: t('Location'), flex:1, minWidth: 150}, 
        { field: 'system', headerName: t('System'), flex:1, minWidth: 150, hide:true}, 
        { field: 'name', headerName: t('Device'), flex:1, minWidth: 220},
        { field: 'key', headerName: t('Key'), flex:1, minWidth: 220, valueFormatter: (v)=>beautifyKey(v.value)}, 
        { field: 'value', headerName: t('Value'), flex:1, minWidth: 100, valueGetter: valueGetter}
    ]


    return (
        <DataGrid
            columns={columns}
            rows={filteredReadings}
            getRowId={(row) => row.url}
        />
    )

}


export default function StatusViewer (props) {

    const {facility} = props;

    const { t } = useTranslation();

    const localTimezone = props.clientInfo.timezone;

    const [readings, setReadings] = React.useState([]);
    const [outputs, setOutputs] = React.useState([]);

    const [activeTab, setActiveTab] = React.useState("floor_plan");

    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");
    const [floorPlans, setFloorPlans] = React.useState([]);
    const [currentFloorPlanURL, setCurrentFloorPlanURL] = React.useState("");
    const [floorPlanEditMode, setFloorPlanEditMode] = React.useState(false);
    const [saveChangesConfirmOpen, setSaveChangesConfirmOpen] = React.useState(false);

    const [latestCallTimestamp, setLatestCallTimestamp] = React.useState(Date.now());

    const [floorPlanChanges, setFloorPlanChanges] = React.useState([]);

    const {readingsApi, pointsApi, floorPlanApi} = React.useContext(ApiContext)

    React.useEffect(() => 
    {
        readingsApi.getLatestReadings(
            facility,
            data => {
                setReadings(data);
            },
            ()=>{
                setReadings([]);
                handleAPIError();
            }
        )

        pointsApi.list(
            facility,
            data => {
                setOutputs(data.filter((item)=>item.category!=="INPUT"));
            },
            ()=>{
                setOutputs([]);
                handleAPIError();
            }
        )
     }, 
    [latestCallTimestamp, facility]
    )

    React.useEffect(() => 
    {
        floorPlanApi.list(
            facility,
            data => {
                setFloorPlans(data);
                setCurrentFloorPlanURL(data.length>0? data[0].url : "")
            },
            ()=>{
                handleAPIError();
            }
        )
     }, 
    [facility]
    )

    const handleAPIError = () => {
        setAlertMessage(t("Problem fetching data."));
        setAlertOpen(true);
    }

    const handleFloorPlanChange = (id, x, y) => {

        var changes = floorPlanChanges.map((item) => {return {...item}});
        const index = changes.findIndex(x => x.id===id); 
        if (index === -1) {
            changes.push({id: id, x: x, y: y})
        }  else {
            changes[index].x = x;
            changes[index].y = y;
        }

        setFloorPlanChanges(changes);

    }
    
    const handlePlanEditModeChange = (event) => {
        if (floorPlanEditMode) {

            setSaveChangesConfirmOpen(true);

            
        } else {
            setFloorPlanEditMode(true);
        }

    }

    const handleSaveChanges = () => {

        floorPlanApi.updatePointFloorPlanLocations(
            floorPlanChanges, 
            () => {
                setFloorPlanEditMode(false); 
                setFloorPlanChanges([]); 
            },
            () => {
                setAlertMessage(t("Couldn't update locations."));
                setAlertOpen(true);
            }
            );
        
    }

    const handleDiscardChanges = () => {
        setFloorPlanChanges([]);
        setLatestCallTimestamp(Date.now())
        setFloorPlanEditMode(false);
    }

    const [floorPlanWidth, setFloorPlanWidth] = React.useState(100);
    const [floorPlanHeight, setFloorPlanHeight] = React.useState(100);

    React.useEffect(() => {
        const resizeObserver = new ResizeObserver((event) => {
            setFloorPlanWidth(event[0].contentBoxSize[0].inlineSize);
            setFloorPlanHeight(event[0].contentBoxSize[0].blockSize);
        });
        if (document.getElementById("floor_plan_container"))
            resizeObserver.observe(document.getElementById("floor_plan_container"));
    });


    return (
        <Stack>

            <Box sx={{ borderBottom: 1, marginBottom:0, borderColor: 'divider' }}>
                    <Tabs value={activeTab} onChange={(event,value)=>setActiveTab(value)}>
                        <Tab label={t("Floor plans")} value="floor_plan" />
                        <Tab label={t("All readings")} value="readings"/>
                    </Tabs>
            </Box>


            <Box           
            sx={{
                backgroundColor: (theme) =>
                theme.palette.mode === 'light'
                    ? theme.palette.grey[100]
                    : theme.palette.grey[900],
                    p:3
            }}>

            
            <Grid container spacing={2}  direction="row" alignItems="stretch">

                { activeTab === "floor_plan" &&
                <Grid item xs={12} lg={9}>
                    <Paper sx={{p: 2, overflow:"auto"}} >
                        <Stack spacing={2}>
                            <Stack direction={{xs: "column", md: "row"}} spacing={2} display="flex">
                                <Typography component="h2" variant="h6" color="primary">
                                    {t("Temperature")}
                                </Typography>
                                
                                <Select 
                                    value={currentFloorPlanURL? currentFloorPlanURL: ""}
                                    size="small"
                                    onChange={(event) => setCurrentFloorPlanURL(event.target.value)}
                                >
                                    {
                                        floorPlans.map(
                                            (item) => <MenuItem key={item.url} value={item.url}>{item.name}</MenuItem>
                                        )
                                    }
                                </Select>
                                <Box flexGrow={1}></Box>                             
                                <ToggleButton
                                    value={floorPlanEditMode}
                                    selected={floorPlanEditMode}
                                    onChange={handlePlanEditModeChange}
                                    size='small'
                                    >
                                        <EditIcon/>
                                </ToggleButton>   
                            </Stack>
                            <Box overflow="hidden" id="floor_plan_container" height='calc(100vh - 249px)'>
                                 <FloorPlanViewer 
                                        floorPlanURL={currentFloorPlanURL? currentFloorPlanURL : ""} 
                                        status={readings}
                                        outputs={outputs}
                                        onChange={handleFloorPlanChange} editable={floorPlanEditMode}
                                        width={floorPlanWidth}
                                        height={floorPlanHeight}
                                />
                            </Box>                          
                        </Stack>
                    </Paper>
                </Grid>
                }
                { activeTab === "floor_plan" &&
                <Grid item xs={12} lg={3} minHeight={500}>
                    <Paper sx={{p: 2, height:"100%", overflow:"auto"}} >
                        <Stack display="flex" height="100%"> 
                            <Stack direction="row" display="flex">
                                <Typography component="h2" variant="h6" color="primary" flexGrow={1}>
                                    {
                                        currentFloorPlanURL? floorPlans.find((item)=>item.url===currentFloorPlanURL)?.name ?? t("Data")
                                        :
                                        t("Data")
                                    }
                                </Typography>
                                <Button onClick={()=>setLatestCallTimestamp(Date.now())}>
                                    <RefreshIcon/>
                                </Button>
                            </Stack>
                            <Typography variant="subtitle2" gutterBottom>
                                {t("Retrieved on")} {readings.length>0? 
                                moment.utc(
                                    readings.reduce((mostRecent, current) => 
                                        new Date(current.timestamp) > new Date(mostRecent.timestamp) ? current : mostRecent
                                    )
                                    ["timestamp"]).tz(localTimezone).format("D MMM, HH:mm")
                                : ""
                                }
                            </Typography>
                            <Box flexGrow={1}>
                                <TemperatureTable temperature={
                                    readings.filter((item)=>currentFloorPlanURL? currentFloorPlanURL.endsWith(item.floor_plan_id + "/") : true)
                                    }
                                />
                            </Box>
                            
                        </Stack>
                    </Paper>
                </Grid>
                }
                { activeTab !== "floor_plan" &&
                <Grid item xs={12} minHeight={500} >
                    <Paper sx={{p: 2, height:'calc(100vh - 161px)', overflow:"auto"}} >
                        <Stack height="100%">
                            <Typography component="h2" variant="h6" color="primary" gutterBottom>
                                {t("All readings")}
                            </Typography>
                            <Box flexGrow={1}>
                                <ReadingsTable readings={readings}/>
                            </Box>
                        </Stack>
                    </Paper>
                </Grid>
                }

            </Grid>

            
            </Box>

            <Dialog
                    open={alertOpen}
                    onClose={()=>setAlertOpen(false)}
                >
                <DialogTitle id="alert-dialog-title">
                {"Problem"}
                </DialogTitle>
                <DialogContent>
                <DialogContentText id="alert-dialog-description">
                    {alertMessage}
                </DialogContentText>
                </DialogContent>
                <DialogActions>
                <Button onClick={()=>setAlertOpen(false)} autoFocus>
                    {t("OK")}
                </Button>
                </DialogActions>
            </Dialog>
            <Dialog
            open={saveChangesConfirmOpen}
            onClose={()=>{handleDiscardChanges(); setSaveChangesConfirmOpen(false);}}
            >
                <DialogTitle id="alert-dialog-title">
                    {t("Confirmation")}
                </DialogTitle>
                <DialogContent>
                <DialogContentText id="alert-dialog-description">
                    {t("Save changes?")}
                </DialogContentText>
                </DialogContent>
                <DialogActions>
                <Button onClick={()=>{
                    setSaveChangesConfirmOpen(false);
                    handleSaveChanges();
                }}>
                    {t("YES")}
                </Button><Button onClick={()=>{handleDiscardChanges(); setSaveChangesConfirmOpen(false);}} autoFocus>
                    {t("NO")}
                </Button>
                </DialogActions>
            </Dialog>

        </Stack>


    )

}